import React, { useContext } from 'react';
import SortableListItem from '@/components/ui/drag-and-drop/sortable-list-item/SortableListItem';
import {
  Dropdown,
  Icon,
  IconButton,
  useResponsiveValue,
} from '@monash/portal-react';
import c from './edit-carpark-row.module.scss';
import ParkType from '../../park-type/ParkType';
import { DROPDOWN_ITEM_CLASSNAME } from '../EditCarparks';
import { AccessibilityContext } from '@monash/portal-frontend-common';
import { CARPARK_BREAKPOINTS, SEARCH_TERM_MAP } from '../../constants';
import { arrayMove } from '@dnd-kit/sortable';

export const EditCarparkDesktopRow = ({
  selectedCarpark,
  selectedCarparks,
  setSelectedCarparks,
  assembledParkingLocations,
  index,
}) => {
  const selectedValues = selectedCarparks.map((carpark) => carpark.value);
  const { setAppLivePoliteMsg, resetAppLiveMsgs } =
    useContext(AccessibilityContext);
  const responsiveSize = useResponsiveValue(CARPARK_BREAKPOINTS);

  const enrichedCarpark = assembledParkingLocations.find((location) => {
    return location.id === selectedCarpark.value;
  });

  const carparkName = enrichedCarpark?.name || 'unselected carpark';
  const isFirst = index === 0;
  const isLast = index === selectedCarparks.length - 1;

  const carparkDropdownItems = assembledParkingLocations
    .map(({ id, name, colour }) => {
      const label = (
        <div className={c.parkTypes}>
          {colour.map((colour) => (
            <ParkType
              key={colour}
              type={colour}
              className={c.parkType}
              hideText={responsiveSize === 'S'}
            />
          ))}
        </div>
      );

      return {
        id,
        text: name,
        value: id,
        label,
        searchString: `${colour} ${SEARCH_TERM_MAP[colour]}`,
      };
    })
    .filter(
      (item) =>
        !selectedValues.includes(item.value) ||
        item.value === selectedCarpark.value
    );

  const deleteCarpark = (id) => {
    resetAppLiveMsgs();

    setTimeout(() => {
      setAppLivePoliteMsg(`${carparkName}, deleted`);
    }, 200);

    setSelectedCarparks((selectedCarpark) =>
      selectedCarpark.filter((carpark) => carpark.id !== id)
    );
  };

  const updateCarparks = (updatedCarpark) => {
    setSelectedCarparks((carparks) => {
      return carparks.map((carpark) => {
        if (carpark.id === updatedCarpark.id) {
          return updatedCarpark;
        }
        return carpark;
      });
    });
  };

  const moveUp = (index) => {
    if (index > 0) {
      setSelectedCarparks((items) => {
        const itemsClone = [...items];
        return arrayMove(itemsClone, index, index - 1);
      });
    }
  };

  const moveDown = (index) => {
    if (index < selectedCarparks.length - 1) {
      setSelectedCarparks((items) => {
        const itemsClone = [...items];
        return arrayMove(itemsClone, index, index + 1);
      });
    }
  };

  return (
    <SortableListItem
      id={selectedCarpark.id}
      className={c.listItem}
      itemLabel={carparkName}
    >
      <Dropdown
        placeholder="Select or search for a carpark location or permit type"
        id="type"
        className={DROPDOWN_ITEM_CLASSNAME}
        onChange={(value) => {
          updateCarparks({ id: selectedCarpark.id, value });
        }}
        value={selectedCarpark.value}
        data={carparkDropdownItems}
        searchable
      />

      <div>
        <IconButton
          onClick={() => moveUp(index)}
          variant="text"
          mode="card"
          icon={Icon.ArrowUp}
          size={24}
          color="var(--card-text-color)"
          aria-label={`Move ${carparkName} up`}
          className={c.arrow}
          disabled={isFirst}
        />

        <IconButton
          onClick={() => moveDown(index)}
          variant="text"
          mode="card"
          icon={Icon.ArrowDown}
          size={24}
          color="var(--card-text-color)"
          aria-label={`Move ${carparkName} down`}
          className={c.arrow}
          disabled={isLast}
        />

        <IconButton
          onClick={() => deleteCarpark(selectedCarpark.id)}
          variant="text"
          mode="card"
          icon={Icon.Trash}
          size={24}
          color="var(--card-text-color)"
          aria-label={`Delete ${carparkName}`}
        />
      </div>
    </SortableListItem>
  );
};
